import type {
  BackendContactCommentDto,
  BackendContactPersonDto,
  BackendInteractionDetailDto,
  BackendInvestorInteractionRowDto,
  BackendInvestorProfileDto,
  BackendInvestorsListDto,
  BackendIrTeamMemberDto,
  ContactComment,
  ContactPerson,
  CreateInteractionRequest,
  Interaction,
  InteractionTypeValue,
  InvestorInteractionRow,
  InvestorInteractionsScope,
  InvestorListItem,
  InvestorProfileResponse,
  IrTeamMember,
  UpdateInteractionRequest,
} from '@/features/investor-management/investor-management.def.ts';

export const transformInvestorProfile = (
  dto: BackendInvestorProfileDto,
): InvestorProfileResponse => ({
  profile: {
    id: dto.id,
    displayId: dto.displayId,
    name: dto.name,
    city: dto.city,
    country: dto.country,
    geography: dto.geography,
    investorType: dto.type as InvestorProfileResponse['profile']['investorType'],
    status: dto.status,
    legalNames: dto.legalNames ?? [],
    accountCaptain: dto.accountCaptain?.name ?? '',
    accountCaptainId: dto.accountCaptain?.id ?? null,
    netCommitment: dto.netCommitment,
    latestNav: dto.latestNav,
  },
});

export const mapContactPerson = (dto: BackendContactPersonDto): ContactPerson => ({
  id: dto.id,
  name: dto.name,
  title: dto.title,
  email: dto.email,
  phone: dto.phone,
  isPrimary: dto.isPrimary,
  isActive: dto.isActive,
});

export const mapContactComment = (dto: BackendContactCommentDto): ContactComment => ({
  id: dto.id,
  text: dto.text,
  createdBy: dto.createdBy,
  createdByDisplayName: dto.createdByDisplayName,
  createdAt: dto.createdAtUtc,
  updatedAt: dto.updatedAt ?? null,
  isEdited: dto.isEdited,
  visibleToRoles: dto.visibleToRoles ?? [],
  visibleToUsernames: dto.visibleToUsernames ?? [],
});

const normalizeInteractionType = (type: string): InteractionTypeValue =>
  type as InteractionTypeValue;

export const mapInteraction = (dto: BackendInteractionDetailDto): Interaction => {
  const contactParticipants = dto.contactParticipants.map((participant) => ({
    contactPersonId: participant.contactPersonId,
    name: participant.name,
    title: participant.title,
    email: participant.email,
    phone: participant.phone,
    isPrimary: participant.isPrimary,
    isActive: participant.isActive,
  }));

  const irTeamParticipants = dto.irTeamParticipants.map((participant) => ({
    username: participant.username,
  }));

  return {
    id: dto.id,
    date: dto.date.slice(0, 10),
    interactionType: normalizeInteractionType(dto.type),
    details: dto.details,
    subject: dto.subject ?? null,
    emailSender: dto.emailSender ?? null,
    emailRecipient: dto.emailRecipient ?? null,
    emailCc: dto.emailCc ?? null,
    plainTextContent: dto.plainTextContent ?? null,
    combinedText: dto.combinedText ?? null,
    sourceInteractionId: dto.sourceInteractionId ?? null,
    createdBy: dto.createdBy,
    createdByDisplayName: dto.createdByDisplayName,
    createdAt: dto.createdAtUtc,
    updatedBy: dto.updatedBy,
    updatedByDisplayName: dto.updatedByDisplayName,
    updatedAt: dto.updatedAt,
    isEdited: dto.isEdited,
    contactParticipants,
    irTeamParticipants,
    participants: [
      ...contactParticipants.map((participant) => ({
        key: `contact-${participant.contactPersonId}`,
        label: participant.name,
        subLabel: participant.title || undefined,
        kind: 'contact' as const,
        isInactive: !participant.isActive,
      })),
      ...irTeamParticipants.map((participant) => ({
        key: `ir-${participant.username}`,
        label: participant.username,
        kind: 'ir-team' as const,
      })),
    ],
  };
};

export const mapIrTeamMember = (dto: BackendIrTeamMemberDto): IrTeamMember => ({
  username: dto.username,
  email: dto.email,
  nickname: dto.nickname,
  department: dto.department,
  region: dto.region,
  roles: dto.roles ?? [],
});

export const mapInvestorListItem = (
  dto: BackendInvestorsListDto['items'][number],
): InvestorListItem => ({
  id: dto.id,
  displayId: dto.displayId,
  name: dto.name,
  city: dto.city,
  country: dto.country,
  geography: dto.geography,
  investorType: dto.type as InvestorListItem['investorType'],
  status: dto.status as InvestorListItem['status'],
  netCommitment: dto.netCommitment,
  latestNav: dto.latestNav,
  contactCount: dto.contactCount,
});

export const mapInvestorInteractionRow = (
  dto: BackendInvestorInteractionRowDto,
): InvestorInteractionRow => ({
  investorId: dto.investorId,
  displayId: dto.displayId,
  name: dto.name,
  investorType: dto.investorType,
  status: dto.status,
  geography: dto.geography,
  commitmentValue: dto.commitmentValue,
  latestInteractionDate: dto.latestInteractionDate ? new Date(dto.latestInteractionDate) : null,
  latestInteractionType: dto.latestInteractionType,
  interactionCount: dto.interactionCount,
  funds: dto.funds ?? [],
});

export const serializeInteractionRequest = ({
  interactionType,
  ...body
}:
  | Omit<CreateInteractionRequest, 'investorId'>
  | Omit<UpdateInteractionRequest, 'investorId' | 'interactionId'>) => ({
  ...body,
  type: interactionType,
});

export const formatIrTeamMemberLabel = (
  member?: { username: string; nickname?: string },
  fallbackUsername?: string,
): string => {
  if (!member) return fallbackUsername ?? '';
  if (member.nickname && member.nickname !== member.username) {
    return `${member.nickname} (${member.username})`;
  }
  return member.username;
};

/** Built with `URLSearchParams` because RTK `params` would comma-join the repeatable list params. */
export const buildInteractionsScopeParams = (scope: InvestorInteractionsScope) => {
  const qs = new URLSearchParams();
  if (scope.minCommitment !== undefined) qs.set('minCommitment', String(scope.minCommitment));
  scope.types?.forEach((v) => qs.append('types', v));
  scope.funds?.forEach((v) => qs.append('funds', v));
  scope.interactionTypes?.forEach((v) => qs.append('interactionTypes', v));
  if (scope.interactionFrom !== undefined) qs.set('interactionFrom', scope.interactionFrom);
  if (scope.interactionTo !== undefined) qs.set('interactionTo', scope.interactionTo);
  return qs;
};
